"use client"

import { useEffect, useState, type ReactNode } from "react"
import { History, Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { PromptDiff } from "@/components/ui/prompt-diff"

/**
 * One row of history, in the shape the dialog renders. Callers map their own version type onto
 * this — an agent prompt carries a provenance `label`, a tool instruction has none to give.
 */
export type PromptVersionEntry = {
  id: string
  text: string
  createdAt: string
  /** Provenance badge. Omitted where the source is not tracked; no caller invents one. */
  label?: ReactNode
  /** A caller's line under the badge, such as why the version was written. */
  note?: ReactNode
  /** A caller's action on this version, shown while it is selected. */
  action?: ReactNode
}

interface PromptHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Newest first. The caller owns the query, so this dialog fits any versioned text. */
  entries: PromptVersionEntry[]
  isLoading: boolean
  error: unknown
  /** Text currently in the editor — the right-hand side of the comparison. */
  currentText: string
  /** Loads the selected text into the editor. Saving stays the parent's action. */
  onRestore: (text: string) => void
  /** False for view-only callers: the editor and its Save button are disabled for them. */
  canRestore: boolean
}

// Locales are pinned so the parts keep this shape regardless of the viewer's browser:
// en-GB orders the date day-month-year, en-US renders the uppercase AM/PM the UI already used.
// en-GB abbreviates September to "Sept"; every other month is three letters.
const VERSION_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
})
const VERSION_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
})

function formatVersionTimestamp(isoDate: string): string {
  const date = new Date(isoDate)

  return `${VERSION_DATE_FORMAT.format(date)}, ${VERSION_TIME_FORMAT.format(date)}`
}

export function PromptHistoryDialog({
  open,
  onOpenChange,
  entries,
  isLoading,
  error,
  currentText,
  onRestore,
  canRestore,
}: PromptHistoryDialogProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showDiff, setShowDiff] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
      setShowDiff(false)
    }
  }, [open])

  const selected = entries.find((entry) => entry.id === selectedId) ?? entries[0]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>
            Every saved version of this text. Restoring loads it into the editor — press Save
            there to apply it.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>
              {error instanceof Error ? error.message : "Failed to load the history."}
            </AlertDescription>
          </Alert>
        ) : entries.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No saved versions yet — the next save starts the history.
          </p>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 sm:grid-cols-[220px_1fr]">
            <VersionList
              entries={entries}
              selectedId={selected?.id ?? null}
              onSelect={(id) => {
                setSelectedId(id)
                setShowDiff(false)
              }}
            />

            <div className="min-w-0 overflow-y-auto rounded-lg border bg-muted/30 p-3">
              {selected && showDiff ? (
                <PromptDiff
                  prev={selected.text}
                  next={currentText}
                  labels={{ prev: "selected version", next: "current editor" }}
                />
              ) : (
                <pre className="whitespace-pre-wrap break-words font-mono text-xs">
                  {selected?.text}
                </pre>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {selected && (
            <>
              {selected.action}
              <Button type="button" variant="outline" onClick={() => setShowDiff((v) => !v)}>
                {showDiff ? "View version" : "Compare with current"}
              </Button>
              {canRestore && (
                <Button
                  type="button"
                  onClick={() => {
                    onRestore(selected.text)
                    onOpenChange(false)
                  }}
                >
                  Restore into editor
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function VersionList({
  entries,
  selectedId,
  onSelect,
}: {
  entries: PromptVersionEntry[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  return (
    <ul className="max-h-48 space-y-1 overflow-y-auto pr-1 sm:max-h-none">
      {entries.map((version) => (
        <li key={version.id}>
          <button
            type="button"
            onClick={() => onSelect(version.id)}
            className={`w-full rounded-md border p-2 text-left text-xs transition-colors ${
              version.id === selectedId ? "border-primary bg-primary/5" : "hover:bg-muted"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <History className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium">
                {formatVersionTimestamp(version.createdAt)}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              {version.label && (
                <Badge variant="secondary" className="font-normal">
                  {version.label}
                </Badge>
              )}
              <span className="text-muted-foreground">{version.text.length} chars</span>
            </div>
            {version.note && <div className="mt-1 text-muted-foreground">{version.note}</div>}
          </button>
        </li>
      ))}
    </ul>
  )
}
